import dayjs from "dayjs";
import ExcelJS from "exceljs";
import { useTranslation } from "react-i18next";
import { FRONTEND_URL } from "../config/config";
import { TYPE_INFO } from "../mocks";
import { DetectedObjectBoundingBox } from "../types/documentation";
import api from "../utils/api";
import {
  combineImages,
  drawDocumentationReferenceCanvas,
  drawPhotoReferenceCanvas,
} from "../utils/canvas";
import { loadImage } from "../utils/loadImage";
import { sanitizeFileName } from "../utils/sanitizeFileName";

interface Documentations {
  [key: string]: [
    {
      frameName: string;
      objects: { id: string; bndbox: DetectedObjectBoundingBox }[];
    },
  ];
}

interface MultiLineCable {
  id: number;
  lvCableMatchId?: string | null;
  lvCableMatchName?: string | null;
  breaker?: string | null;
  circuit?: string | null;
  sourceName?: string | null;
}

export interface DetectedObject {
  id: string;
  _id: number;
  type: string;
  status: string;
  elementId?: number;
  position: {
    x: number;
    y: number;
  };
  frameReference: string;
  detectionDate: string;
  description?: string;
  position_description?: string;
  chessboard_id?: string;
  documentations: Array<{
    documentationId: number;
    referenceFrames: string[];
    framesPath?: string | null;
  }>;
  cables?: MultiLineCable[];
  [key: string]: any;
}

interface OrphanedCable {
  id: number;
  floorPlanElementId: number;
  frameId: number;
  lvCableMatchId?: string | null;
  lvCableMatchName?: string | null;
  breaker?: string | null;
  circuit?: string | null;
  sourceName?: string | null;
  createdAt: string;
  floorPlanElement?: {
    id: number;
    dwgId?: string;
    positionDescription?: string;
    gridCols?: any;
    gridRows?: any;
  };
}

interface ExcelRowData {
  positionNumber: string;
  lvDescription: string;
  positionDescription: string;
  chessboardId: string;
  locationRoom: string;
  planNr: string;
  breaker?: string;
  circuit?: string;
  source?: string;
  photoReference: string;
  measurementDate: string;
  remarks: string;
  _originalObject: DetectedObject;
  _imageData: string;
  _planViewUrl: string;
  _photoReferenceUrl: string;
  _isCableRow?: boolean;
}

interface SummaryData {
  [type: string]: {
    count: number;
  };
}

interface ExcelExportReturn {
  downloadExcel: (
    detectedObjects: DetectedObject[],
    isPrint: boolean,
    documentations: Documentations,
  ) => Promise<void>;
  generateExcelData: (
    detectedObjects: DetectedObject[],
    isPrint: boolean,
    documentations: Documentations,
  ) => Promise<{
    excelData: ExcelRowData[];
    filteredObjects: DetectedObject[];
    orphanedCables: OrphanedCable[];
    cablesByElementId: Map<number, MultiLineCable[]>;
  }>;
  generateSummaryData: (
    filteredObjects: DetectedObject[],
    orphanedCables: OrphanedCable[],
    cablesByElementId: Map<number, MultiLineCable[]>,
  ) => SummaryData;
}

export const useExcelExport = ({
  planName,
  projectId,
  sectionId,
  planPreviewUrl,
}: {
  planName: string;
  projectId: string;
  sectionId: string;
  planPreviewUrl: string;
}): ExcelExportReturn => {
  const { t } = useTranslation();

  // Helper function to convert base64 to buffer (browser-compatible)
  const base64ToBuffer = (base64String: string): ArrayBuffer => {
    const base64Data = base64String.split(",")[1]; // Remove the data:image/jpeg;base64, part

    // Convert base64 to binary string
    const binaryString = window.atob(base64Data);

    // Convert binary string to Uint8Array
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return bytes.buffer; // Return ArrayBuffer
  };

  // Helper function to get image extension from base64
  const getImageExtension = (base64String: string): "jpeg" | "png" | "gif" => {
    const match = base64String.match(/data:image\/([^;]+);base64/);
    const extension = match ? match[1] : "jpeg";
    return ["jpeg", "png", "gif"].includes(extension)
      ? (extension as "jpeg" | "png" | "gif")
      : "jpeg";
  };

  const addQueryParam = (url: string, key: string, value: string): string => {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
  };

  const generateExcelData = async (
    detectedObjects: DetectedObject[],
    _isPrint: boolean = false,
    documentations: Documentations,
  ): Promise<{
    excelData: ExcelRowData[];
    filteredObjects: DetectedObject[];
    orphanedCables: OrphanedCable[];
    cablesByElementId: Map<number, MultiLineCable[]>;
  }> => {
    const filteredObjects: DetectedObject[] = detectedObjects.filter(
      (obj: DetectedObject) =>
        obj.status === "installed" || obj.status === "real_vs_plan_dev",
    );

    const elementIds = Array.from(
      new Set(
        filteredObjects
          .map((obj) => obj.elementId)
          .filter((id): id is number => typeof id === "number"),
      ),
    );

    const cablesByElementId = new Map<number, MultiLineCable[]>();
    if (elementIds.length > 0) {
      const cableResults = await api.getMultiLineCablesByElementIds(elementIds);
      if (Array.isArray(cableResults)) {
        cableResults.forEach(
          (item: { floorPlanElementId: number; cables: MultiLineCable[] }) => {
            if (typeof item?.floorPlanElementId === "number") {
              cablesByElementId.set(item.floorPlanElementId, item.cables || []);
            }
          },
        );
      }
    }

    // Fetch orphaned cables
    let orphanedCables: OrphanedCable[] = [];
    if (filteredObjects.length > 0) {
      try {
        const lastDocumentation =
          filteredObjects[0].documentations[
            filteredObjects[0].documentations.length - 1
          ];
        if (lastDocumentation?.documentationId) {
          orphanedCables = await api.getOrphanedCables(
            lastDocumentation.documentationId,
          );
        }
      } catch (error) {
        console.error("Error fetching orphaned cables:", error);
        // Continue without orphaned cables if fetch fails
      }
    }

    let img = new Image();
    const frameImages: { [path: string]: HTMLImageElement } = {};

    // Load all images (needed for both print and digital where we embed images)
    const imagePromises = [
      loadImage(planPreviewUrl).then((image) => ({ key: "floorplan", image })),
    ];

    // Add frame image promises
    filteredObjects.forEach((obj: DetectedObject) => {
      const lastDocumentation =
        obj.documentations[obj.documentations.length - 1];
      const lastReferenceFrame =
        lastDocumentation.referenceFrames[
          lastDocumentation.referenceFrames.length - 1
        ];

      const imagePath = api.getFrameImageUrl(
        projectId,
        lastDocumentation,
        lastReferenceFrame + ".jpg",
      );

      imagePromises.push(
        loadImage(imagePath).then((image) => ({ key: imagePath, image })),
      );
    });

    // Wait for all images to load
    const imageResults = await Promise.all(imagePromises);
    // Assign loaded images
    imageResults.forEach((result) => {
      if (result.key === "floorplan") {
        img = result.image;
      } else {
        frameImages[result.key] = result.image;
      }
    });

    // Map filtered objects to Excel data format
    const excelData: ExcelRowData[] = [];
    for (const obj of filteredObjects) {
      // Generate unique Pos.-Nr starting from 1.1.1
      // Handle photo reference based on print mode
      // Prepare photo reference image and URL

      const planObjId = obj.status === "real_vs_plan_dev" ? -obj._id : obj._id;
      const planViewUrl = `${FRONTEND_URL}/project/${projectId}/sections/${sectionId}/progress-report/view?object=${planObjId}`;

      const lastDocumentation =
        obj.documentations[obj.documentations.length - 1];
      const lastReferenceFrame =
        lastDocumentation.referenceFrames[
          lastDocumentation.referenceFrames.length - 1
        ];

      // Build combined image for both modes
      const imagePath = api.getFrameImageUrl(
        projectId,
        lastDocumentation,
        lastReferenceFrame + ".jpg",
      );
      const frameImage = frameImages[imagePath];

      const currentDocumentation =
        documentations[lastDocumentation.documentationId];

      const currentObjects = currentDocumentation.find(
        (frame) => frame.frameName === lastReferenceFrame + ".jpg",
      )?.objects;
      const bbox = currentObjects?.find(
        (detectedObject) => detectedObject.id === String(obj._id),
      )?.bndbox;

      if (
        !frameImage ||
        (frameImage.naturalWidth === 0 && frameImage.width === 0)
      ) {
        throw new Error("Invalid frame image");
      }

      const planBase64 = drawPhotoReferenceCanvas(img, obj);
      const frameBase64 = drawDocumentationReferenceCanvas(
        frameImage,
        bbox,
        obj.status === "installed",
      );
      const combinedBase64 = await combineImages(planBase64, frameBase64);
      const photoReference = combinedBase64;

      const photoReferenceUrl = `${FRONTEND_URL}/project/${projectId}/sections/${sectionId}/documentation/${lastDocumentation.documentationId}/view?frame=${lastReferenceFrame}.jpg&object=${obj._id}`;

      const elementRow: ExcelRowData = {
        positionNumber: obj.boqItemPosition || obj.lvItemId || "",
        // TODO: Legacy type, should be removed
        lvDescription:
          (obj.status === "real_vs_plan_dev"
            ? obj.boqItemName
            : obj.boqItemName || (TYPE_INFO as any)[obj.type]?.legend_name) ||
          "Unknown",
        positionDescription: obj.position_description || "",
        chessboardId: obj.chessboard_id || "",
        locationRoom: "5.1",
        planNr: "MEL_5152_EG_ELT051_A_Z",
        breaker: "",
        circuit: "",
        source: "",
        photoReference,
        measurementDate: dayjs
          .utc(obj.detectionDate)
          .format("YYYY-MM-DD HH:mm"),
        remarks: obj.description || "",
        // Keep track of the original object for image handling
        _originalObject: obj,
        _imageData: photoReference,
        _planViewUrl: planViewUrl,
        _photoReferenceUrl: photoReferenceUrl,
      };

      excelData.push(elementRow);

      const cables =
        (typeof obj.elementId === "number"
          ? cablesByElementId.get(obj.elementId)
          : undefined) || [];

      for (const cable of cables) {
        const cableRow: ExcelRowData = {
          positionNumber: cable.lvCableMatchId || "",
          lvDescription: cable.lvCableMatchName || "",
          positionDescription: obj.position_description || "",
          chessboardId: obj.chessboard_id || "",
          locationRoom: "5.1",
          planNr: "MEL_5152_EG_ELT051_A_Z",
          breaker: cable.breaker || "",
          circuit: cable.circuit || "",
          source: cable.sourceName || "",
          photoReference: "",
          measurementDate: dayjs
            .utc(obj.detectionDate)
            .format("YYYY-MM-DD HH:mm"),
          remarks: "",
          _originalObject: obj,
          _imageData: "",
          _planViewUrl: addQueryParam(planViewUrl, "showShortestPath", "true"),
          _photoReferenceUrl: photoReferenceUrl,
          _isCableRow: true,
        };

        excelData.push(cableRow);
      }
    }

    // Add orphaned cables at the end
    for (const orphanedCable of orphanedCables) {
      const orphanedCableRow: ExcelRowData = {
        positionNumber: orphanedCable.lvCableMatchId || "",
        lvDescription: orphanedCable.lvCableMatchName || "",
        positionDescription:
          orphanedCable.floorPlanElement?.positionDescription || "",
        chessboardId: orphanedCable.floorPlanElement?.gridCols
          ? `${orphanedCable.floorPlanElement.gridCols}-${orphanedCable.floorPlanElement.gridRows}`
          : "",
        locationRoom: "5.1",
        planNr: "MEL_5152_EG_ELT051_A_Z",
        breaker: orphanedCable.breaker || "",
        circuit: orphanedCable.circuit || "",
        source: orphanedCable.sourceName || "",
        photoReference: "",
        measurementDate: dayjs
          .utc(orphanedCable.createdAt)
          .format("YYYY-MM-DD HH:mm"),
        remarks: "",
        _originalObject: {} as DetectedObject,
        _imageData: "",
        _planViewUrl: "",
        _photoReferenceUrl: "",
        _isCableRow: true,
      };

      excelData.push(orphanedCableRow);
    }

    return { excelData, filteredObjects, orphanedCables, cablesByElementId };
  };

  const generateSummaryData = (
    filteredObjects: DetectedObject[],
    orphanedCables: OrphanedCable[],
    cablesByElementId: Map<number, MultiLineCable[]>,
  ): SummaryData => {
    const summaryData: SummaryData = {};

    filteredObjects.forEach((obj: DetectedObject) => {
      const type = obj.type;

      if (!summaryData[type]) {
        summaryData[type] = {
          count: 0,
        };
      }

      summaryData[type].count++;
    });

    // Count cables as a separate type
    let cableCount = orphanedCables.length;
    for (const cables of cablesByElementId.values()) {
      cableCount += cables.length;
    }
    if (cableCount > 0) {
      summaryData["Cable"] = {
        count: cableCount,
      };
    }

    return summaryData;
  };

  const createWorksheet = async (
    workbook: ExcelJS.Workbook,
    excelData: ExcelRowData[],
    summaryData: SummaryData,
    isPrint: boolean = false,
  ): Promise<ExcelJS.Worksheet> => {
    const sheetName = isPrint ? "Output Results for Print" : "Output Results";
    const worksheet = workbook.addWorksheet(sheetName);

    // Define columns with proper headers
    const columns = [
      { header: t("export.position_nr"), key: "positionNumber", width: 25 },
      { header: t("export.lv_description"), key: "lvDescription", width: 25 },
      {
        header: t("export.position_description"),
        key: "positionDescription",
        width: 35,
      },
      { header: t("export.chessboard_id"), key: "chessboardId", width: 20 },
      { header: t("export.location_room"), key: "locationRoom", width: 25 },
      { header: t("export.plan_nr"), key: "planNr", width: 25 },
      { header: t("export.breaker"), key: "breaker", width: 18 },
      { header: t("export.circuit"), key: "circuit", width: 18 },
      { header: t("export.source"), key: "source", width: 22 },
      { header: t("export.photo_reference"), key: "photoReference", width: 60 },
      ...(isPrint
        ? []
        : [
            {
              header: t("export.photo_reference"),
              key: "photoReferenceUrl",
              width: 25,
            },
          ]),
      ...(isPrint
        ? []
        : [
            {
              header: t("export.photo_reference"),
              key: "planReference",
              width: 25,
            },
          ]),
      {
        header: t("export.measurement_date"),
        key: "measurementDate",
        width: 25,
      },
      { header: t("export.remarks"), key: "remarks", width: 30 },
    ];

    worksheet.columns = columns;

    // Style the header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF2F5233" }, // Dark green
    };
    headerRow.alignment = { horizontal: "center", vertical: "middle" };
    headerRow.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
    headerRow.height = 25;

    // Add data rows
    excelData.forEach((rowData: ExcelRowData, index: number) => {
      const rowObj: any = {
        positionNumber: rowData.positionNumber,
        lvDescription: rowData.lvDescription,
        positionDescription: t(`export.${rowData.positionDescription}`),
        chessboardId: rowData.chessboardId,
        locationRoom: rowData.locationRoom,
        planNr: rowData.planNr,
        breaker: rowData.breaker || "",
        circuit: rowData.circuit || "",
        source: rowData.source || "",
        photoReference: "", // Will be replaced with image
        measurementDate: rowData.measurementDate,
        remarks: rowData.remarks,
      };

      // Only add planReference for digital version
      if (!isPrint) {
        rowObj.photoReferenceUrl = t("export.view_documentation");
        rowObj.planReference = t("export.view_plan"); // Will be replaced with link
      }

      const row = worksheet.addRow(rowObj);

      // Style data rows
      const isEven = (index + 2) % 2 === 0; // +2 because we start from row 2 (after header)
      const fillColor = isEven ? "FFF8F9FA" : "FFFFFFFF";

      row.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: fillColor },
      };

      row.alignment = { horizontal: "left", vertical: "middle" };
      row.border = {
        top: { style: "thin", color: { argb: "FFE9ECEF" } },
        left: { style: "thin", color: { argb: "FFE9ECEF" } },
        bottom: { style: "thin", color: { argb: "FFE9ECEF" } },
        right: { style: "thin", color: { argb: "FFE9ECEF" } },
      };

      // Handle photo reference column
      const photoCell = row.getCell("photoReference");
      const photoUrlCell = !isPrint ? row.getCell("photoReferenceUrl") : null;
      const planCell = !isPrint ? row.getCell("planReference") : null;

      if (rowData._isCableRow) {
        photoCell.value = "";
      } else if (
        rowData._imageData &&
        rowData._imageData.startsWith("data:image/")
      ) {
        try {
          // Add image to workbook
          const imageBuffer = base64ToBuffer(rowData._imageData);
          const extension = getImageExtension(rowData._imageData);

          const imageId = workbook.addImage({
            buffer: imageBuffer,
            extension: extension,
          });

          // Set row height to accommodate image
          row.height = 140;

          // Add image to cell
          worksheet.addImage(imageId, {
            tl: { col: 6, row: index + 1 },
            ext: { width: 360, height: 120 },
          });

          // Clear the cell content since we're adding an image
          photoCell.value = "";
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Failed to add image:", error);
          photoCell.value = t("export.image_error");
          photoCell.font = { italic: true, color: { argb: "FFFF0000" } };
        }
      } else {
        photoCell.value = t("export.no_image_available");
        photoCell.font = { italic: true, color: { argb: "FF666666" } };
      }

      // Add hyperlinks only in digital mode
      if (!isPrint) {
        if (photoUrlCell) {
          photoUrlCell.value = {
            text: t("export.view_documentation"),
            hyperlink: rowData._photoReferenceUrl,
          };
          photoUrlCell.font = { color: { argb: "FF0066CC" }, underline: true };
          photoUrlCell.alignment = { horizontal: "center", vertical: "middle" };
        }
        if (planCell) {
          planCell.value = {
            text: t("export.view_plan"),
            hyperlink: rowData._planViewUrl,
          };
          planCell.font = { color: { argb: "FF0066CC" }, underline: true };
          planCell.alignment = { horizontal: "center", vertical: "middle" };
        }
      }
    });

    // Add final report section
    const startRow = excelData.length + 3;

    // Add "Final Report" header
    const reportHeaderRow = worksheet.getRow(startRow);
    reportHeaderRow.getCell(1).value = t("export.final_report");
    reportHeaderRow.getCell(1).font = { bold: true, size: 14 };
    reportHeaderRow.getCell(1).alignment = {
      horizontal: "left",
      vertical: "middle",
    };

    // Add summary headers
    const summaryHeaderRow = worksheet.getRow(startRow + 2);
    const summaryHeaders = [
      t("export.object_type"),
      t("export.quantity"),
      t("export.unit"),
    ];

    summaryHeaders.forEach((header: string, index: number) => {
      const cell = summaryHeaderRow.getCell(index + 1);
      cell.value = header;
      cell.font = { bold: true };
      cell.alignment = { horizontal: "left", vertical: "middle" };
    });

    // Add summary data
    let currentSummaryRow = startRow + 3;
    const summaryEntries = Object.entries(summaryData);

    if (summaryEntries.length > 0) {
      summaryEntries.forEach(([type, data]) => {
        const row = worksheet.getRow(currentSummaryRow);
        row.getCell(1).value =
          (TYPE_INFO as any)[type]?.legend_name || "Unknown";
        row.getCell(2).value = data.count;
        row.getCell(2).alignment = { horizontal: "right", vertical: "middle" };
        row.getCell(3).value = t("export.pieces");
        currentSummaryRow++;
      });
    } else {
      const row = worksheet.getRow(currentSummaryRow);
      row.getCell(1).value = "No data";
      row.getCell(2).value = 0;
      row.getCell(3).value = t("export.pieces");
      currentSummaryRow++;
    }

    // Add grand total
    const grandTotalCount = Object.values(summaryData).reduce(
      (sum, data) => sum + data.count,
      0,
    );

    const totalRow = worksheet.getRow(currentSummaryRow + 1);
    totalRow.getCell(1).value = t("export.total");
    totalRow.getCell(1).font = { bold: true };
    totalRow.getCell(2).value = grandTotalCount;
    totalRow.getCell(2).font = { bold: true };
    totalRow.getCell(2).alignment = { horizontal: "right", vertical: "middle" };

    // Set freeze panes
    worksheet.views = [
      {
        state: "frozen",
        xSplit: 0,
        ySplit: 1,
        topLeftCell: "A2",
        activeCell: "A2",
      },
    ];

    // Set auto filter
    const lastColumn = worksheet.getColumn(columns.length).letter;
    worksheet.autoFilter = {
      from: "A1",
      to: `${lastColumn}${excelData.length + 1}`,
    };

    return worksheet;
  };

  const downloadExcel = async (
    detectedObjects: DetectedObject[],
    isPrint: boolean = false,
    documentations: Documentations,
  ): Promise<void> => {
    try {
      const { excelData, filteredObjects, orphanedCables, cablesByElementId } =
        await generateExcelData(detectedObjects, isPrint, documentations);

      if (excelData.length === 0) {
        // eslint-disable-next-line no-console
        console.warn("No data to export");
        return;
      }

      // Create workbook
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Monco Application";
      workbook.lastModifiedBy = "Monco Application";
      workbook.created = new Date();
      workbook.modified = new Date();

      const summaryData = generateSummaryData(
        filteredObjects,
        orphanedCables,
        cablesByElementId,
      );

      // Create worksheet with data and styling
      await createWorksheet(workbook, excelData, summaryData, isPrint);

      // Generate filename
      const currentDate = new Date().toISOString().split("T")[0];
      const sectionNameClean = sanitizeFileName({ rawName: planName });
      const reportPrefix = sanitizeFileName({ rawName: t("export.report") });
      const printSuffix = isPrint ? "_Print" : "";
      const filename = `${reportPrefix}${printSuffix}_${sectionNameClean}_${currentDate}.xlsx`;

      // Download the file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error generating Excel file:", error);
      throw error;
    }
  };

  return {
    downloadExcel,
    generateExcelData,
    generateSummaryData,
  };
};
